import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useJobDetailsProvider } from '../../../../../../context/jobDetails';
import { useGlobalSearchProvider } from '../../../../../../context/globalSearch';
import DataTable from '../../../../../../../components/Common/UI/DataTable';
import geoDeliveryCol from './columns';
import {
  GeoDeliveryColumnEnum,
  GeoDeliveryColumnType,
  getSubTableColConfig,
  JobDetailColumnType,
  JobSubTableAccount,
  jobSubTableAccountsData,
  jobTypesPaper,
} from '../subTableConfig';
import { geoDeliveryData, GeoDeliveryRow } from './geoDeliveryData';
import USMap from '../../../../../../../components/Common/Map/USMap';

function GeoDeliveryData() {
  const { id = '' } = useParams<{ id: string }>();
  const { selectedTable } = useJobDetailsProvider();
  const { searchApplied } = useGlobalSearchProvider();

  const [geoDeliveryState, setGeoDeliveryState] = useState<GeoDeliveryRow[]>([]);
  const [jobSubTableData, setJobSubTableData] = useState<JobSubTableAccount[]>([]);

  /** Set data in main & sub tables on init */
  useEffect(() => {
    setGeoDeliveryState(geoDeliveryData);
  }, [geoDeliveryData]);
  useEffect(() => {
    setJobSubTableData(jobSubTableAccountsData);
  }, [jobSubTableAccountsData]);

  const handleFilterSubTable = (data: JobSubTableAccount[], types: JobDetailColumnType[]) => {
    const newData = data.filter(account => types.includes(account.jobType));

    setJobSubTableData(newData);
  };

  /** When user select table cell to which show more data, filter sub-table data depending on 'selectedTable' state */
  useEffect(() => {
    const { column, row } = selectedTable;

    if (row === 'INSERTS' && column === 'total') {
      setJobSubTableData(jobSubTableAccountsData.filter(item => item.accountName === 'inserts'));

      return;
    }

    if (column === 'totalMail') {
      setJobSubTableData(
        jobSubTableAccountsData.filter(item => item.delivery && item.delivery !== 'Transit' && item.state === row),
      );
    } else {
      setJobSubTableData(
        jobSubTableAccountsData.filter(
          item => item.state === row && item.delivery === GeoDeliveryColumnEnum[column as GeoDeliveryColumnType],
        ),
      );
    }

    if (!column || column === 'total') {
      // This page works only with paper columns at the moment| id === 'Electronic' ? jobTypesElectronic : jobTypesPaper
      handleFilterSubTable(jobSubTableAccountsData, jobTypesPaper);
    }
  }, [selectedTable.column, selectedTable.row]);

  /** Filter current data thru global search before show */
  const filteredThruSearchData = useMemo(() => {
    const { search: appliedSearch, field } = searchApplied;

    /** If both search and field-key exist, then do simple comparison by single key. */
    if (appliedSearch && field) {
      return jobSubTableData.filter(item =>
        String(item[field]).toLowerCase().includes(String(appliedSearch).toLowerCase()),
      );
    }

    /** If key not specified then search in each object value */
    if (appliedSearch) {
      const searchString = String(appliedSearch).toLowerCase();

      return jobSubTableData.filter(item =>
        Object.values(item).some(
          value =>
            // Searching for only string & number values, avoiding null & undefined
            (typeof value === 'string' && value.toLowerCase().includes(searchString)) ||
            (typeof value === 'number' && String(value).includes(searchString)),
        ),
      );
    }

    return jobSubTableData;
  }, [jobSubTableData, searchApplied]);

  return (
    <div className="flex flex-col gap-4">
      <div className="mx-4 mt-4 grid grid-cols-2 gap-4">
        <DataTable
          columns={geoDeliveryCol}
          data={geoDeliveryState}
          variant="bordered"
          paginationPosition="bottom"
          initialState={{ pagination: { pageSize: 5 } }}
          className="rounded"
        />

        <div className="max-h-96 bg-slate-100 rounded-lg border flex items-center justify-center">
          <USMap />
        </div>
      </div>

      <div className="h-[24px]" />
      {selectedTable.column ? (
        <div>
          <DataTable
            columns={getSubTableColConfig(id, selectedTable.row, true)}
            data={filteredThruSearchData}
            variant="bordered"
            paginationPosition="bottom"
          />
        </div>
      ) : null}
    </div>
  );
}

export default GeoDeliveryData;
